import { useState } from 'react'
import { useSelector } from 'react-redux'
import { ArrowLeft } from 'lucide-react'

import MdmSetupStep from './MdmSetupStep'

import styles from './MdmRolloutSection.module.scss'
import settingsStyles from '@/pages/app/Settings/styles.module.scss'

import Button from '@/components/Button'
import DialogWrapper from '@/components/Dialog'
import EnterpriseDownloads from '@/pages/app/Enterprises/components/EnterpriseDownloads'
import { mdmProviderLabel, mdmUpdateIntentLabel } from '@/pages/app/Settings/mdmLabels'
import { selectIsAdmin } from '@/store/features/user/userSlice'
import { MdmProvider, MdmUpdateIntent } from '@/types'

export interface MdmDeclarationProps {
  providers: MdmProvider[]
  updateIntent: MdmUpdateIntent | null
  onSave: (providers: MdmProvider[], updateIntent: MdmUpdateIntent) => Promise<boolean>
}

const MdmRolloutSection = ({ providers, updateIntent, onSave }: MdmDeclarationProps) => {
  const isAdmin = useSelector(selectIsAdmin)
  const [isOpen, setIsOpen] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [draftProviders, setDraftProviders] = useState<MdmProvider[]>(providers)
  const [draftUpdateIntent, setDraftUpdateIntent] = useState<MdmUpdateIntent | null>(updateIntent)

  const declaredIntent = providers.length > 0 ? updateIntent : null
  // Only admins can declare, so everyone else goes straight to the guide. While a save is in flight the props
  // already hold the optimistic value, so the step stays up until the save is known to have worked.
  const isShowingSetup = isAdmin && (isSaving || declaredIntent === null || isEditing)

  const loadDrafts = () => {
    setDraftProviders(providers)
    setDraftUpdateIntent(updateIntent)
  }

  const open = () => {
    loadDrafts()
    setIsEditing(false)
    setIsOpen(true)
  }

  const startEditing = () => {
    loadDrafts()
    setIsEditing(true)
  }

  const handleContinue = async () => {
    if (draftUpdateIntent === null || draftProviders.length === 0) return
    setIsSaving(true)
    try {
      if (await onSave(draftProviders, draftUpdateIntent)) setIsEditing(false)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className={settingsStyles.settingSection}>
      <div className={settingsStyles.settingInfo}>
        <div className={settingsStyles.settingTitle}>
          MDM Rollout <span className={styles.optional}>(Optional)</span>
        </div>
        <div className={settingsStyles.settingDescription}>
          For admins using an MDM provider. Roll Wispr Flow out to your team and control how it updates over time.
        </div>
      </div>
      <Button title="Start MDM Setup" onClick={open} type="dark" className={settingsStyles.controlButton} />
      <DialogWrapper
        isOpen={isOpen}
        title={
          isShowingSetup ? (
            'Tell us how you manage Wispr Flow'
          ) : (
            <>
              MDM rollout <span className={styles.titleOptional}>(optional)</span>
            </>
          )
        }
        close={() => setIsOpen(false)}
        minWidth={'min(90vw, 760px)'}
      >
        {isShowingSetup ? (
          <MdmSetupStep
            providers={draftProviders}
            updateIntent={draftUpdateIntent}
            isSaving={isSaving}
            onProvidersChange={setDraftProviders}
            onUpdateIntentChange={setDraftUpdateIntent}
            onContinue={handleContinue}
            onCancel={declaredIntent !== null ? () => setIsEditing(false) : undefined}
          />
        ) : (
          <>
            {isAdmin && declaredIntent !== null && (
              <button className={styles.backButton} onClick={startEditing}>
                <ArrowLeft size={16} aria-hidden="true" />
                Back
              </button>
            )}
            {declaredIntent !== null && (
              <div className={styles.summary}>
                <span className={styles.summaryText}>
                  <span className={styles.summaryLabel}>Declared:</span> {providers.map(mdmProviderLabel).join(', ')} ·{' '}
                  {mdmUpdateIntentLabel(declaredIntent)} updates
                </span>
              </div>
            )}
            <EnterpriseDownloads />
          </>
        )}
      </DialogWrapper>
    </div>
  )
}

export default MdmRolloutSection
